import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiArrowUp } from "react-icons/fi";
import { PRIVACY_POLICY, PolicySection, PolicySectionId } from "./policyContent";
import { PolicyBlockView } from "./PolicyBlocks";
import PolicyToc from "./PolicyToc";

// Site-wide rules that previously lived in privacyPolicy.css; kept so other pages look unchanged.
import "./legacyGlobal.css";
import "./privacyPolicy.css";

const PAGE_TITLE = "Privacy Policy | Nexcaliber";
const META_DESCRIPTION =
	"How Nexcaliber collects, uses, discloses and protects information through the Nexcaliber Member App, including account and data deletion.";
/** Added to <body> while this page is mounted, so print rules only apply here. */
const BODY_CLASS = "pp-page-active";
const BACK_TO_TOP_THRESHOLD = 640;
/** Extra gap between the sticky site header and a scrolled-to heading (matches the CSS). */
const SCROLL_GAP = 24;

const { title, effectiveDate, lastUpdated, intro, sections } = PRIVACY_POLICY;
const SECTION_IDS = new Set<string>(sections.map((section: PolicySection) => section.id));
const isSectionId = (value: string): value is PolicySectionId => SECTION_IDS.has(value);

const prefersReducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

const scrollBehavior = (smooth: boolean): ScrollBehavior => (smooth && !prefersReducedMotion() ? "smooth" : "auto");

/** Scrolls a section heading to just below the sticky header (offset comes from CSS scroll-margin-top). */
const scrollToSection = (id: PolicySectionId, smooth: boolean) => {
	document.getElementById(id)?.scrollIntoView({ behavior: scrollBehavior(smooth), block: "start" });
};

/** Sets the tab title and meta description while the page is mounted, restoring them afterwards. */
const usePageMeta = () => {
	useEffect(() => {
		const previousTitle = document.title;
		document.title = PAGE_TITLE;

		let meta = document.querySelector<HTMLMetaElement>("meta[name='description']");
		const createdMeta = !meta;
		if (!meta) {
			meta = document.createElement("meta");
			meta.name = "description";
			document.head.appendChild(meta);
		}
		const previousDescription = meta.content;
		meta.content = META_DESCRIPTION;

		document.body.classList.add(BODY_CLASS);

		return () => {
			document.title = previousTitle;
			if (createdMeta) meta?.remove();
			else if (meta) meta.content = previousDescription;
			document.body.classList.remove(BODY_CLASS);
		};
	}, []);
};

const PrivacyPolicy = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const rootRef = useRef<HTMLElement>(null);
	const titleRef = useRef<HTMLHeadingElement>(null);
	const articleRef = useRef<HTMLElement>(null);
	const intersectingIds = useRef(new Set<PolicySectionId>());
	/** Set when we update the URL hash ourselves, so the hash effect doesn't scroll a second time. */
	const skipNextHashScroll = useRef(false);

	const [activeId, setActiveId] = useState<PolicySectionId>(sections[0].id);
	const [showBackToTop, setShowBackToTop] = useState(false);

	usePageMeta();

	const updateActiveSection = useCallback(() => {
		const doc = document.documentElement;
		const atBottom = window.innerHeight + window.scrollY >= doc.scrollHeight - 4;
		if (atBottom) {
			setActiveId(sections[sections.length - 1].id);
			return;
		}
		const first = sections.find((section: PolicySection) => intersectingIds.current.has(section.id));
		if (first) setActiveId(first.id);
	}, []);

	// Track which section is in the reading band just below the sticky header.
	useEffect(() => {
		const root = rootRef.current;
		const article = articleRef.current;
		if (!root || !article || typeof IntersectionObserver === "undefined") return;

		const headerOffset = parseFloat(getComputedStyle(root).getPropertyValue("--pp-header-offset")) || 0;
		const topInset = Math.round(headerOffset + SCROLL_GAP);
		const visible = intersectingIds.current;

		const observer = new IntersectionObserver(
			(entries: IntersectionObserverEntry[]) => {
				entries.forEach((entry: IntersectionObserverEntry) => {
					const id = (entry.target as HTMLElement).dataset.sectionId ?? "";
					if (!isSectionId(id)) return;
					if (entry.isIntersecting) visible.add(id);
					else visible.delete(id);
				});
				updateActiveSection();
			},
			{ rootMargin: `-${topInset}px 0px -55% 0px`, threshold: 0 }
		);

		article
			.querySelectorAll<HTMLElement>("[data-section-id]")
			.forEach((element: HTMLElement) => observer.observe(element));
		return () => {
			observer.disconnect();
			visible.clear();
		};
	}, [updateActiveSection]);

	// Back-to-top visibility and the end-of-page active section.
	useEffect(() => {
		let frame = 0;
		const onScroll = () => {
			if (frame) return;
			frame = window.requestAnimationFrame(() => {
				frame = 0;
				setShowBackToTop(window.scrollY > BACK_TO_TOP_THRESHOLD);
				updateActiveSection();
			});
		};
		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		window.addEventListener("resize", onScroll);
		return () => {
			window.removeEventListener("scroll", onScroll);
			window.removeEventListener("resize", onScroll);
			window.cancelAnimationFrame(frame);
		};
	}, [updateActiveSection]);

	// Deep links: /privacy-policy#account-deletion on a fresh load or from in-app navigation.
	// Runs after the app's ScrollToTop effect, which resets the scroll position on route changes.
	useEffect(() => {
		if (skipNextHashScroll.current) {
			skipNextHashScroll.current = false;
			return;
		}
		const id = decodeURIComponent(location.hash.replace(/^#/, ""));
		if (!isSectionId(id)) return;

		scrollToSection(id, false);
		setActiveId(id);

		// Re-align once web fonts and late layout settle, unless the user has scrolled since.
		let cancelled = false;
		const landedAt = window.scrollY;
		const realign = () => {
			if (!cancelled && Math.abs(window.scrollY - landedAt) < 2) scrollToSection(id, false);
		};
		document.fonts?.ready.then(realign).catch(() => undefined);
		if (document.readyState !== "complete") window.addEventListener("load", realign, { once: true });

		return () => {
			cancelled = true;
			window.removeEventListener("load", realign);
		};
	}, [location.hash]);

	const handleNavigate = useCallback(
		(id: PolicySectionId) => {
			scrollToSection(id, true);
			document.getElementById(id)?.focus({ preventScroll: true });
			setActiveId(id);
			if (location.hash !== `#${id}`) {
				skipNextHashScroll.current = true;
				navigate({ hash: id }, { replace: true });
			}
		},
		[location.hash, navigate]
	);

	const handleBackToTop = () => {
		window.scrollTo({ top: 0, behavior: scrollBehavior(true) });
		titleRef.current?.focus({ preventScroll: true });
		if (location.hash) navigate({ hash: "" }, { replace: true });
	};

	return (
		<main className="pp-root" ref={rootRef}>
			<header className="pp-hero">
				<div className="pp-shell">
					<h1 className="pp-title" id="pp-title" ref={titleRef} tabIndex={-1}>
						{title}
					</h1>
					<div className="pp-meta">
						<p>
							<strong>Effective Date:</strong> {effectiveDate}
						</p>
						<p>
							<strong>Last Updated:</strong> {lastUpdated}
						</p>
					</div>
				</div>
			</header>

			<div className="pp-shell pp-layout">
				<PolicyToc sections={sections} activeId={activeId} onNavigate={handleNavigate} />

				<article className="pp-article" aria-labelledby="pp-title" ref={articleRef}>
					<div className="pp-intro">
						{intro.map((block, index) => (
							<PolicyBlockView key={index} block={block} />
						))}
					</div>

					{sections.map((section: PolicySection) => (
						<section
							key={section.id}
							className={`pp-section${section.emphasis ? " pp-section--emphasis" : ""}`}
							aria-labelledby={section.id}
							data-section-id={section.id}
						>
							<h2 className="pp-section-title" id={section.id} tabIndex={-1}>
								<span className="pp-section-number">{section.number}.</span> {section.title}
							</h2>
							{section.blocks.map((block, index) => (
								<PolicyBlockView key={index} block={block} />
							))}
						</section>
					))}
				</article>
			</div>

			<button
				type="button"
				className={`pp-back-to-top${showBackToTop ? " is-visible" : ""}`}
				onClick={handleBackToTop}
				tabIndex={showBackToTop ? 0 : -1}
			>
				<FiArrowUp aria-hidden="true" />
				<span className="pp-back-to-top-label">Back to top</span>
			</button>
		</main>
	);
};

export default PrivacyPolicy;
